import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { useAdminBranding, useUpdateWelcomeMessage, useWelcomeMessage } from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { cn } from "@/lib/utils";
import type { WelcomeMessageSettings } from "@/services/gpsdrive-welcome-message.service";

export const Route = createFileRoute("/admin/communication")({
  component: () => (
    <AdminAccessGate action="comunicacao">
      <CommunicationPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Mensagem de Boas-Vindas — Administração — Wp Rastreamento" }] }),
});

interface FormState {
  initialMessage: string;
  loginUrl: string;
  sendVehicleData: boolean;
  sendAppLinks: boolean;
  appleStoreUrl: string;
  googlePlayUrl: string;
  appsMessage: string;
  contactsMessage: string;
  emergencyPhone: string;
  supportWhatsapp: string;
  finalMessage: string;
}

// Exemplos usados só como ponto de partida (nunca salvos automaticamente)
// quando a empresa ainda não configurou nada — assim que houver qualquer
// valor salvo no backend, ele sempre vence sobre isto. `companyName` vem
// do Nome do servidor/empresa já configurado em Identidade Visual (dado
// real da empresa logada), com um fallback genérico só pra quando isso
// também nunca foi preenchido.
function defaultsFor(companyName: string): FormState {
  return {
    initialMessage: `SEJA MUITO BEM-VINDO(A) À *${companyName}*\n\n👋 Estamos felizes por tê-lo(a) conosco!\n\nVamos garantir que você e seu veículo estejam sempre protegidos com a melhor tecnologia.`,
    loginUrl: "https://sistema.suaempresa.com.br",
    sendVehicleData: false,
    sendAppLinks: false,
    appleStoreUrl: "",
    googlePlayUrl: "",
    appsMessage: "Fique conectado(a) e tenha total controle do seu veículo em qualquer lugar!",
    contactsMessage: "Veículo roubado ou necessita de alguma assistência? Entre em contato imediatamente!",
    emergencyPhone: "0800 511 7000 (Telefone 24h)",
    supportWhatsapp: "(51) 2501-1908 WhatsApp (Horário comercial)",
    finalMessage: `✨ Mais do que segurança, nós oferecemos tranquilidade. Conte conosco! ✨\n\n🚀 A tecnologia que cuida de você!\n\n${companyName}`,
  };
}

function toFormState(data: WelcomeMessageSettings | undefined, companyName: string): FormState {
  const fallback = defaultsFor(companyName);
  if (!data) return fallback;
  return {
    initialMessage: data.initialMessage || fallback.initialMessage,
    loginUrl: data.loginUrl ?? fallback.loginUrl,
    sendVehicleData: data.sendVehicleData,
    sendAppLinks: data.sendAppLinks,
    appleStoreUrl: data.appleStoreUrl ?? fallback.appleStoreUrl,
    googlePlayUrl: data.googlePlayUrl ?? fallback.googlePlayUrl,
    appsMessage: data.appsMessage ?? fallback.appsMessage,
    contactsMessage: data.contactsMessage ?? fallback.contactsMessage,
    emergencyPhone: data.emergencyPhone ?? fallback.emergencyPhone,
    supportWhatsapp: data.supportWhatsapp ?? fallback.supportWhatsapp,
    finalMessage: data.finalMessage ?? fallback.finalMessage,
  };
}

function CommunicationPage() {
  const { data: branding } = useAdminBranding();
  const { data: welcomeMessage, isLoading } = useWelcomeMessage();
  const update = useUpdateWelcomeMessage();
  const companyName = branding?.serverName?.trim() || "Sua Empresa";

  const [form, setForm] = useState<FormState>(() => defaultsFor(companyName));

  useEffect(() => {
    setForm(toFormState(welcomeMessage, companyName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [welcomeMessage]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.initialMessage.trim()) {
      toast.error("Preencha a mensagem de boas-vindas");
      return;
    }
    try {
      await update.mutateAsync({
        initialMessage: form.initialMessage,
        loginUrl: form.loginUrl || null,
        sendVehicleData: form.sendVehicleData,
        sendAppLinks: form.sendAppLinks,
        appleStoreUrl: form.appleStoreUrl || null,
        googlePlayUrl: form.googlePlayUrl || null,
        appsMessage: form.appsMessage || null,
        contactsMessage: form.contactsMessage || null,
        emergencyPhone: form.emergencyPhone || null,
        supportWhatsapp: form.supportWhatsapp || null,
        finalMessage: form.finalMessage || null,
      });
      toast.success("Mensagem de boas-vindas atualizada");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar a mensagem de boas-vindas");
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mensagem de Boas-Vindas</h1>
        <p className="text-sm text-muted-foreground">
          Personalize a mensagem enviada a novos clientes de {companyName}.
        </p>
      </div>

      <GpsDriveSessionBanner />

      <div className="grid flex-1 gap-6 lg:grid-cols-2 lg:items-start">
        <Card className="rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Configuração</CardTitle>
            <CardDescription>Tudo que aparece no preview ao lado pode ser personalizado aqui.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="wm-initial">Mensagem de Boas-Vindas</Label>
              <Textarea
                id="wm-initial"
                rows={8}
                value={form.initialMessage}
                onChange={(e) => set("initialMessage", e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Formatação estilo WhatsApp: <code>*texto*</code> = negrito, <code>_texto_</code> = itálico.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wm-login-url">URL Login</Label>
              <Input
                id="wm-login-url"
                value={form.loginUrl}
                onChange={(e) => set("loginUrl", e.target.value)}
                placeholder="https://sistema.suaempresa.com.br"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="wm-send-vehicle-data"
                  checked={form.sendVehicleData}
                  onCheckedChange={(v) => set("sendVehicleData", !!v)}
                  disabled={isLoading}
                />
                <Label htmlFor="wm-send-vehicle-data" className="cursor-pointer font-normal">
                  Enviar dados do veículo
                </Label>
              </div>
              <p className="pl-6 text-xs text-muted-foreground">
                No preview ao lado mostra um exemplo de como ficaria — os dados reais do veículo do
                cliente ainda não são preenchidos automaticamente no envio de verdade.
              </p>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="wm-send-app-links"
                  checked={form.sendAppLinks}
                  onCheckedChange={(v) => set("sendAppLinks", !!v)}
                  disabled={isLoading}
                />
                <Label htmlFor="wm-send-app-links" className="cursor-pointer font-normal">
                  Enviar links dos aplicativos
                </Label>
              </div>

              {form.sendAppLinks && (
                <div className="space-y-4 border-l-2 border-border pl-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="wm-apple-url">Link da Apple Store</Label>
                      <Input
                        id="wm-apple-url"
                        value={form.appleStoreUrl}
                        onChange={(e) => set("appleStoreUrl", e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wm-google-url">Link do Google Play</Label>
                      <Input
                        id="wm-google-url"
                        value={form.googlePlayUrl}
                        onChange={(e) => set("googlePlayUrl", e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wm-apps-message">Mensagem de Apps</Label>
                    <Textarea
                      id="wm-apps-message"
                      rows={3}
                      value={form.appsMessage}
                      onChange={(e) => set("appsMessage", e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="wm-contacts-message">Mensagem dos Contatos</Label>
              <Textarea
                id="wm-contacts-message"
                rows={3}
                value={form.contactsMessage}
                onChange={(e) => set("contactsMessage", e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="wm-emergency-phone">Telefone de Emergência</Label>
                <Input
                  id="wm-emergency-phone"
                  value={form.emergencyPhone}
                  onChange={(e) => set("emergencyPhone", e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wm-support-whatsapp">Número do WhatsApp Suporte</Label>
                <Input
                  id="wm-support-whatsapp"
                  value={form.supportWhatsapp}
                  onChange={(e) => set("supportWhatsapp", e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="wm-final-message">Mensagem Final</Label>
              <Textarea
                id="wm-final-message"
                rows={6}
                value={form.finalMessage}
                onChange={(e) => set("finalMessage", e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSave} disabled={update.isPending || isLoading}>
                {update.isPending ? "Salvando…" : "Salvar"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="lg:sticky lg:top-6">
          <WhatsAppPreview form={form} />
        </div>
      </div>
    </div>
  );
}

// Converte a formatação estilo WhatsApp (*negrito*, _itálico_) em JSX —
// mesma sintaxe que o campo aceita, pra o preview refletir exatamente como
// o cliente veria a mensagem chegando de verdade.
function formatWhatsAppText(text: string): ReactNode {
  const lines = text.split("\n");
  return lines.map((line, lineIndex) => (
    <span key={lineIndex}>
      {formatLine(line)}
      {lineIndex < lines.length - 1 && <br />}
    </span>
  ));
}

function formatLine(line: string): ReactNode[] {
  const regex = /\*(.+?)\*|_(.+?)_/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
    if (match[1] !== undefined) parts.push(<strong key={key++}>{match[1]}</strong>);
    else if (match[2] !== undefined) parts.push(<em key={key++}>{match[2]}</em>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < line.length) parts.push(line.slice(lastIndex));
  return parts;
}

function PreviewDivider() {
  return <div className="my-3 border-t border-dashed border-black/10" />;
}

function WhatsAppPreview({ form }: { form: FormState }) {
  return (
    <Card className="overflow-hidden rounded-2xl border-none py-0 shadow-sm">
      <div className="flex items-center gap-3 bg-[#008069] px-4 py-3 text-white">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-base">🚗</div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">Mensagem de Boas-Vindas</p>
          <p className="text-xs text-white/80">preview em tempo real</p>
        </div>
      </div>

      <div
        className="max-h-[70vh] overflow-y-auto p-4"
        style={{
          backgroundColor: "#e5ddd5",
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(0,0,0,0.03) 1px, transparent 1px), radial-gradient(circle at 60% 70%, rgba(0,0,0,0.03) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      >
        <div className="max-w-full rounded-lg bg-white px-3 py-2 text-sm leading-relaxed text-neutral-800 shadow-sm">
          <p>🎉 <strong>OLÁ, CLIENTE!</strong></p>
          <p className="mt-2 whitespace-pre-line">{formatWhatsAppText(form.initialMessage)}</p>

          {form.sendVehicleData && (
            <>
              <PreviewDivider />
              <p>🚗 <strong>DADOS DO SEU VEÍCULO</strong></p>
              <p className="mt-2">🚘 Modelo: Ex: Fiat Toro</p>
              <p>🔖 Placa: ABC-1234</p>
              <p>📡 Rastreador: instalado e ativo</p>
            </>
          )}

          <PreviewDivider />

          <p>🌐 Acesse nossa plataforma online e acompanhe seu veículo de onde estiver:</p>
          <p className="mt-1 break-all text-[#008069]">👉 {form.loginUrl || "URL DO LOGIN"}</p>
          <p className="mt-2">🔐 Seus dados de acesso à plataforma:</p>
          <p className="mt-1">👤 Usuário: exemplo@email.com</p>
          <p>🔑 Senha: sua senha</p>

          {form.sendAppLinks && (
            <>
              <PreviewDivider />
              <p>📱 <strong>TENHA TUDO NA PALMA DA MÃO COM NOSSO APP!</strong></p>
              <p className="mt-2">📥 Baixe agora mesmo:</p>
              <p className="mt-2">🤖 Android:</p>
              <p className="break-all text-[#008069]">{form.googlePlayUrl || "LINK GOOGLE PLAY"}</p>
              <p className="mt-2">🍎 iOS:</p>
              <p className="break-all text-[#008069]">{form.appleStoreUrl || "LINK APPLE STORE"}</p>
              {form.appsMessage && <p className="mt-2 whitespace-pre-line">{formatWhatsAppText(form.appsMessage)}</p>}
            </>
          )}

          <PreviewDivider />

          <p>🆘 <strong>ESTAMOS AQUI PARA AJUDAR!</strong></p>
          {form.contactsMessage && (
            <p className="mt-2 whitespace-pre-line">{formatWhatsAppText(form.contactsMessage)}</p>
          )}
          <p className="mt-2">📞 {form.emergencyPhone || "Telefone de emergência"}</p>
          <p>📱 {form.supportWhatsapp || "WhatsApp de suporte"}</p>

          <PreviewDivider />

          <p className="whitespace-pre-line">{formatWhatsAppText(form.finalMessage)}</p>

          <p className={cn("mt-2 flex items-center justify-end gap-1 text-[10px] text-neutral-400")}>
            12:00 <CheckCheck className="h-3.5 w-3.5 text-[#53bdeb]" />
          </p>
        </div>
      </div>
    </Card>
  );
}
